/**
 * The Three Hundred — Judges 6–7 mission ladder.
 * Teaches Doubt (scaffold → full onion) while Midian is Fear/Despair.
 */
import type { ScenarioDef } from "../../data/scenario";
import type { MissionDef } from "../types";
import { G1, G2, G3, G4, G5, G6, G7 } from "./lieSkins";
import {
  STORY_FLEECE,
  STORY_JERUBBAAL,
  STORY_JEZREEL,
  STORY_PITCHERS,
  STORY_SHALOM,
  STORY_THREE_HUNDRED,
  STORY_WINEPRESS,
} from "./story";

/** G1 — Core-only Doubt + light Fear (winepress). */
const WINEPRESS: ScenarioDef = {
  id: "gid-winepress",
  name: "By the Winepress",
  duration: 95,
  handSize: 5,
  maxIntegrity: 125,
  disableConfusion: true,
  spawns: [
    {
      at: 4,
      lieId: "whisper",
      label: "Hide the wheat",
      skin: G1.midianHides,
    },
    {
      at: 14,
      lieId: "nagging-question",
      label: "If the Lord be with us…",
      skin: G1.ifWithUs,
      doubtScaffold: "core",
    },
    { at: 28, lieId: "whisper", skin: G1.leastInHouse },
    {
      at: 40,
      lieId: "nagging-question",
      skin: G1.ifWithUs,
      doubtScaffold: "core",
    },
    { at: 55, lieId: "whisper", layer: "heart", skin: G1.leastInHouse },
    {
      at: 68,
      lieId: "nagging-question",
      label: "Why has this befallen us?",
      skin: G1.ifWithUs,
      doubtScaffold: "core",
    },
    { at: 82, lieId: "whisper", skin: G1.midianHides },
  ],
};

/** G2 — Half onion + peace after the sign. */
const SHALOM: ScenarioDef = {
  id: "gid-jehovah-shalom",
  name: "Jehovah-shalom",
  duration: 90,
  handSize: 5,
  maxIntegrity: 120,
  disableConfusion: true,
  spawns: [
    {
      at: 6,
      lieId: "nagging-question",
      label: "Shew me a sign",
      skin: G2.needASign,
      doubtScaffold: "half",
    },
    { at: 22, lieId: "whisper", skin: G2.fearDeath },
    {
      at: 38,
      lieId: "nagging-question",
      skin: G2.needASign,
      doubtScaffold: "half",
    },
    { at: 54, lieId: "whisper", layer: "heart", skin: G2.fearDeath },
    {
      at: 70,
      lieId: "nagging-question",
      label: "Peace — and still the land burns",
      skin: G2.needASign,
      doubtScaffold: "half",
    },
  ],
};

/** G3 — Night Baal; Fear of people + full Doubt. */
const JERUBBAAL: ScenarioDef = {
  id: "gid-jerubbaal",
  name: "Jerubbaal",
  duration: 100,
  handSize: 5,
  maxIntegrity: 100,
  disableConfusion: true,
  spawns: [
    {
      at: 4,
      lieId: "alone",
      label: "The city will kill you",
      skin: G3.cityWillKill,
    },
    {
      at: 16,
      lieId: "nagging-question",
      layer: "heart",
      skin: G3.cityWillKill,
      doubtScaffold: "full",
    },
    { at: 28, lieId: "whisper", skin: G3.nightOnly },
    { at: 40, lieId: "alone", layer: "heart", skin: G3.cityWillKill },
    {
      at: 52,
      lieId: "nagging-question",
      skin: G3.nightOnly,
      doubtScaffold: "full",
    },
    { at: 64, lieId: "heavy-gloom", skin: G3.baalStands },
    { at: 76, lieId: "alone", layer: "heart", label: "Morning comes", skin: G3.cityWillKill },
    {
      at: 88,
      lieId: "nagging-question",
      layer: "heart",
      skin: G3.cityWillKill,
      doubtScaffold: "full",
    },
  ],
};

/** G4 — Midian host; Fear tempo, little new Doubt. */
const JEZREEL: ScenarioDef = {
  id: "gid-jezreel",
  name: "Valley of Jezreel",
  duration: 105,
  handSize: 5,
  maxIntegrity: 90,
  spawns: [
    {
      at: 3,
      lieId: "wildfire",
      label: "As grasshoppers",
      skin: G4.grasshopperHost,
    },
    { at: 12, lieId: "whisper", skin: G4.cannotStand },
    { at: 22, lieId: "heavy-gloom", skin: G4.withoutNumber },
    { at: 32, lieId: "wildfire", layer: "heart", skin: G4.grasshopperHost },
    { at: 42, lieId: "whisper", skin: G4.cannotStand },
    { at: 52, lieId: "alone", layer: "will", skin: G4.cannotStand },
    { at: 62, lieId: "wildfire", skin: G4.grasshopperHost },
    { at: 72, lieId: "heavy-gloom", layer: "heart", skin: G4.withoutNumber },
    { at: 82, lieId: "whisper", layer: "will", skin: G4.cannotStand },
    {
      at: 92,
      lieId: "wildfire",
      label: "The Spirit and the trumpet",
      skin: G4.grasshopperHost,
    },
  ],
};

/** G5 — Fleece masterclass: full onion, two claim waves. */
const FLEECE: ScenarioDef = {
  id: "gid-fleece",
  name: "The Fleece",
  duration: 100,
  handSize: 5,
  maxIntegrity: 115,
  disableConfusion: true,
  spawns: [
    {
      at: 5,
      lieId: "nagging-question",
      label: "Prove it",
      skin: G5.proveIt,
      doubtScaffold: "full",
    },
    { at: 22, lieId: "whisper", skin: G1.leastInHouse },
    {
      at: 36,
      lieId: "nagging-question",
      skin: G5.proveIt,
      doubtScaffold: "full",
    },
    {
      at: 52,
      lieId: "nagging-question",
      label: "But this once",
      skin: G5.onceMore,
      doubtScaffold: "full",
    },
    { at: 68, lieId: "whisper", layer: "heart", skin: G1.leastInHouse },
    {
      at: 82,
      lieId: "nagging-question",
      skin: G5.onceMore,
      doubtScaffold: "full",
    },
  ],
};

/** G6 — Too many / 300; Despair + Gather teach + light Doubt. */
const THREE_HUNDRED: ScenarioDef = {
  id: "gid-three-hundred",
  name: "Three Hundred",
  duration: 110,
  handSize: 5,
  maxIntegrity: 95,
  disableConfusion: true,
  spawns: [
    {
      at: 4,
      lieId: "heavy-gloom",
      label: "Too many — then too few",
      skin: G6.tooFew,
    },
    { at: 14, lieId: "whisper", skin: G6.fearfulLeave },
    {
      at: 26,
      lieId: "nagging-question",
      skin: G6.ownHand,
      doubtScaffold: "half",
    },
    { at: 38, lieId: "heavy-gloom", layer: "heart", skin: G6.tooFew },
    { at: 50, lieId: "whisper", layer: "will", skin: G6.fearfulLeave },
    {
      at: 62,
      lieId: "heavy-gloom",
      label: "Three hundred",
      skin: G6.tooFew,
    },
    {
      at: 74,
      lieId: "nagging-question",
      layer: "will",
      skin: G6.ownHand,
      doubtScaffold: "full",
    },
    { at: 86, lieId: "alone", skin: G6.fearfulLeave },
    { at: 98, lieId: "heavy-gloom", layer: "will", skin: G6.tooFew },
  ],
};

/** G7 — Capstone: mixed Fear + sparse Doubt; victory proof. */
const PITCHERS: ScenarioDef = {
  id: "gid-pitchers",
  name: "Sword of the Lord",
  duration: 115,
  handSize: 5,
  maxIntegrity: 85,
  spawns: [
    {
      at: 3,
      lieId: "wildfire",
      label: "Look at the host",
      skin: G7.lookAtTheHost,
    },
    {
      at: 12,
      lieId: "nagging-question",
      label: "If thou fear…",
      skin: G7.ifThouFear,
      doubtScaffold: "full",
    },
    { at: 24, lieId: "whisper", skin: G7.lookAtTheHost },
    { at: 36, lieId: "heavy-gloom", skin: G7.barleyEcho },
    { at: 48, lieId: "wildfire", layer: "heart", skin: G7.lookAtTheHost },
    { at: 58, lieId: "alone", layer: "will", skin: G7.ifThouFear },
    {
      at: 68,
      lieId: "nagging-question",
      layer: "will",
      skin: G7.ifThouFear,
      doubtScaffold: "half",
    },
    { at: 78, lieId: "wildfire", skin: G7.lookAtTheHost },
    { at: 88, lieId: "whisper", layer: "heart", skin: G7.lookAtTheHost },
    {
      at: 98,
      lieId: "heavy-gloom",
      layer: "will",
      label: "Every man in his place",
      skin: G7.barleyEcho,
    },
    { at: 108, lieId: "wildfire", skin: G7.lookAtTheHost },
  ],
};

/**
 * Faith-forward teaching loadouts early; pool later.
 * G1 guarantees Faith + Redeem path for core Doubt.
 */
export const GIDEON_MISSIONS: readonly MissionDef[] = [
  {
    id: "gid-winepress",
    index: 0,
    name: "By the Winepress",
    blurb:
      "If the Lord be with us, why…? Meet Doubt as a bare question — Faith Answer or Redeem settles it. Wrong Word tangles shells.",
    story: STORY_WINEPRESS,
    scenario: WINEPRESS,
    rewardCards: ["ps-56-3", "rom-10-17", "mark-11-22"],
    loadout: {
      mode: "fixed",
      core: [
        "heb-11-1",
        "mark-11-22",
        "ps-56-3",
        "rom-10-17",
        "gen-50-20",
        "phil-4-6-7",
      ],
    },
  },
  {
    id: "gid-jehovah-shalom",
    index: 1,
    name: "Jehovah-shalom",
    blurb:
      "Shew me a sign. Peel the outer shell, then settle the core. Peace tools calm fear after the fire.",
    story: STORY_SHALOM,
    scenario: SHALOM,
    rewardCards: ["mark-4-39", "john-14-27", "thess-5-17"],
    loadout: {
      mode: "fixed+carryIn",
      core: [
        "heb-11-1",
        "mark-11-22",
        "ps-56-3",
        "mark-4-39",
        "gen-50-20",
        "phil-4-6-7",
      ],
      carryInFromMissionId: "gid-winepress",
      maxCarryIn: 3,
    },
  },
  {
    id: "gid-jerubbaal",
    index: 2,
    name: "Jerubbaal",
    blurb:
      "Tear down Baal by night. Fear of people hits Heart — full Doubt with a timer. Still holds the question open while you obey.",
    story: STORY_JERUBBAAL,
    scenario: JERUBBAAL,
    rewardCards: ["2cor-10-5", "col-3-15", "eph-6-16"],
    loadout: {
      mode: "fixed+carryIn",
      core: [
        "heb-11-1",
        "john-14-27",
        "ps-56-3",
        "2cor-10-5",
        "rom-10-17",
        "phil-4-6-7",
      ],
      carryInFromMissionId: "gid-jehovah-shalom",
      maxCarryIn: 3,
    },
  },
  {
    id: "gid-jezreel",
    index: 3,
    name: "Valley of Jezreel",
    blurb:
      "Midian as grasshoppers. Hold the line with Fear and Despair tools you know — the Spirit gathers the people.",
    story: STORY_JEZREEL,
    scenario: JEZREEL,
    rewardCards: ["eph-6-17", "isa-40-31", "mark-11-23-24"],
    loadout: {
      mode: "fixed+carryIn",
      core: [
        "heb-11-1",
        "john-14-27",
        "eph-6-16",
        "mark-4-39",
        "rom-15-13",
        "phil-4-6-7",
      ],
      carryInFromMissionId: "gid-jerubbaal",
      maxCarryIn: 3,
    },
  },
  {
    id: "gid-fleece",
    index: 4,
    name: "The Fleece",
    blurb:
      "Prove it — then prove it once more. Full onion masterclass. God answers a trembling servant twice.",
    story: STORY_FLEECE,
    scenario: FLEECE,
    rewardCards: ["rom-8-28", "isa-26-3", "ps-46-10"],
    loadout: {
      mode: "pool",
      core: [
        "heb-11-1",
        "mark-11-22",
        "ps-56-3",
        "rom-10-17",
        "gen-50-20",
        "phil-4-6-7",
      ],
      minPoolSize: 6,
    },
  },
  {
    id: "gid-three-hundred",
    index: 5,
    name: "Three Hundred",
    blurb:
      "Too many, then too few. Gather residual Prayers (Eccl 4:9–10). Despair says the remnant is nothing — God is stripping boasting.",
    story: STORY_THREE_HUNDRED,
    scenario: THREE_HUNDRED,
    rewardCards: ["ps-91-1-2", "prov-3-6", "neh-8-10"],
    loadout: {
      mode: "pool",
      core: [
        "heb-11-1",
        "eccl-4-9-10",
        "rom-15-13",
        "thess-5-17",
        "gen-50-20",
        "phil-4-6-7",
      ],
      minPoolSize: 8,
    },
  },
  {
    id: "gid-pitchers",
    index: 6,
    name: "Sword of the Lord",
    blurb:
      "If thou fear, go down and hear the dream. Then stand — trumpets, pitchers, lamps. God proves the claim with three hundred against a multitude.",
    story: STORY_PITCHERS,
    scenario: PITCHERS,
    rewardCards: ["eccl-4-9-10", "eph-6-17", "isa-40-31"],
    loadout: {
      mode: "pool",
      core: [
        "heb-11-1",
        "eph-6-17",
        "gen-50-20",
        "eccl-4-9-10",
        "ps-91-1-2",
        "mark-11-22",
      ],
      minPoolSize: 10,
    },
  },
];

export function gideonMissionById(id: string): MissionDef | undefined {
  return GIDEON_MISSIONS.find((m) => m.id === id);
}
